/**
 * Centralized Error Handling Utility
 * Provides user-friendly error messages and logs technical details
 */

export interface AppError {
  message: string;
  code?: string;
  statusCode?: number;
  originalError?: any;
}

/**
 * Maps backend error codes to user-friendly messages
 */
const ERROR_MESSAGES: Record<string, string> = {
  // Authentication errors
  'UNAUTHORIZED': 'Session expired. Please login again.',
  'FORBIDDEN': 'You do not have permission to perform this action.',
  'INVALID_CREDENTIALS': 'Invalid email or password. Please try again.',
  'EMAIL_NOT_VERIFIED': 'Please verify your email before logging in.',
  'ACCOUNT_INACTIVE': 'Your account is inactive. Please contact support.',
  'ACCOUNT_NOT_APPROVED': 'Your account is pending approval. Please wait for admin approval.',
  
  // Network errors
  'NETWORK_ERROR': 'Network issue. Please check your internet connection.',
  'TIMEOUT': 'Request timed out. Please try again.',
  'SERVICE_UNAVAILABLE': 'Service temporarily unavailable. Please try again later.',
  
  // Validation errors
  'VALIDATION_ERROR': 'Please check your input and try again.',
  'INVALID_EMAIL': 'Please enter a valid email address.',
  'WEAK_PASSWORD': 'Password is too weak. Please use a stronger password.',
  'REQUIRED_FIELD': 'Please fill in all required fields.',
  
  // Generic errors
  'NOT_FOUND': 'The requested resource was not found.',
  'CONFLICT': 'This action conflicts with existing data.',
  'INTERNAL_ERROR': 'Something went wrong. Please try again.',
};

/**
 * Extracts user-friendly error message from various error types
 */
export const getErrorMessage = (error: any): string => {
  // Log the original error for debugging
  console.error('Error details:', error);
  
  // If it's already a formatted error message
  if (typeof error === 'string') {
    return error;
  }
  
  // Check for error code first
  if (error?.errorCode && ERROR_MESSAGES[error.errorCode]) {
    return ERROR_MESSAGES[error.errorCode];
  }
  
  // Check for status code
  if (error?.statusCode || error?.status) {
    const status = error.statusCode || error.status;
    
    switch (status) {
      case 401:
        return ERROR_MESSAGES['UNAUTHORIZED'];
      case 403:
        return ERROR_MESSAGES['FORBIDDEN'];
      case 404:
        return ERROR_MESSAGES['NOT_FOUND'];
      case 409:
        return ERROR_MESSAGES['CONFLICT'];
      case 500:
      case 502:
      case 503:
        return ERROR_MESSAGES['SERVICE_UNAVAILABLE'];
      default:
        break;
    }
  }
  
  // Check for network errors
  if (error?.name === 'TypeError' && error?.message?.includes('fetch')) {
    return ERROR_MESSAGES['NETWORK_ERROR'];
  }
  
  // Check for timeout
  if (error?.name === 'TimeoutError' || error?.message?.includes('timeout')) {
    return ERROR_MESSAGES['TIMEOUT'];
  }
  
  // Check for message in error object
  if (error?.message) {
    // If it's a user-friendly message, return it
    if (Object.values(ERROR_MESSAGES).includes(error.message)) {
      return error.message;
    }
    
    // For technical errors, return generic message
    // but log the actual error
    return ERROR_MESSAGES['INTERNAL_ERROR'];
  }
  
  // Default fallback
  return ERROR_MESSAGES['INTERNAL_ERROR'];
};

/**
 * Handles API errors and returns formatted error
 */
export const handleApiError = (error: any): AppError => {
  const message = getErrorMessage(error);
  
  return {
    message,
    code: error?.errorCode,
    statusCode: error?.statusCode || error?.status,
    originalError: error,
  };
};

/**
 * Validates email format
 */
export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};

/**
 * Validates password strength
 */
export const validatePassword = (password: string): { valid: boolean; message?: string } => {
  if (password.length < 8) {
    return { valid: false, message: 'Password must be at least 8 characters long' };
  }
  
  if (!/[A-Z]/.test(password)) {
    return { valid: false, message: 'Password must contain at least one uppercase letter' };
  }
  
  if (!/[a-z]/.test(password)) {
    return { valid: false, message: 'Password must contain at least one lowercase letter' };
  }
  
  if (!/[0-9]/.test(password)) {
    return { valid: false, message: 'Password must contain at least one number' };
  }
  
  return { valid: true };
};

/**
 * Trims whitespace from form values
 */
export const trimFormData = <T extends Record<string, any>>(data: T): T => {
  const trimmed: any = {};
  
  Object.keys(data).forEach(key => {
    const value = data[key];
    if (typeof value === 'string') {
      trimmed[key] = value.trim();
    } else {
      trimmed[key] = value;
    }
  });
  
  return trimmed as T;
};

